// elevation-catalog.ts
// The catalog GET /api/elevations serves, and the one request that reads it.

import { ELEVATIONS_API } from './variables';

export interface CatalogAsset {
  fileId?: string;
  url: string;
  alt?: string | null;
}

export interface Pointer {
  id: string;
  slug: string;
  name: string;
}

export interface CatalogItem {
  id: string;
  slug: string;
  name: string;
  fields: Record<string, unknown>;
}

export interface CatalogElevation extends CatalogItem {
  plan: Pointer | null;
  roof: Pointer | null;
  siding: Pointer | null;
  style: Pointer | null;
}

export type Dimension = 'plan' | 'roof' | 'siding' | 'style';

export interface CatalogTiming {
  ms: number;
  webflowMs: number;
  reads: number;
  hits: number;
}

export interface Catalog {
  cmsState: 'live' | 'staged';
  elevations: CatalogElevation[];
  homePlans: CatalogItem[];
  roofs: CatalogItem[];
  sidings: CatalogItem[];
  styles: CatalogItem[];
  timing: CatalogTiming;
  freshGranted?: boolean;
  nextFreshSeconds?: number;
}

export interface FetchCatalogOptions {
  baseUrl?: string;
  /** Read Webflow now; the page URL's `?__fresh__` sets this by default. */
  fresh?: boolean;
  debug?: boolean;
}

/** Whether `value` is an asset as Webflow sends one: an object with a url. */
export function isAsset(value: unknown): value is CatalogAsset {
  return typeof value === 'object' && value !== null && typeof (value as { url?: unknown }).url === 'string';
}

const pageFlag = (name: string): boolean => {
  if (typeof window === 'undefined') return false;

  return new URLSearchParams(window.location.search).has(name);
};

/**
 * One request for the catalog. A failed request returns null, so a page
 * keeps the markup Webflow shipped.
 */
export async function fetchCatalog(options: FetchCatalogOptions = {}): Promise<Catalog | null> {
  const base = options.baseUrl || ELEVATIONS_API;
  const fresh = options.fresh ?? pageFlag('__fresh__');
  const debug = options.debug ?? pageFlag('__debug__');
  const url = fresh ? `${base}${base.includes('?') ? '&' : '?'}fresh` : base;

  try {
    const response = await fetch(url, { headers: { accept: 'application/json' } });

    if (!response.ok) {
      if (debug) console.warn('elevation-catalog: request failed', response.status);
      return null;
    }

    const catalog = (await response.json()) as Catalog;

    if (debug) {
      console.log('elevation-catalog ·', catalog.cmsState, catalog.elevations.length, 'elevations', catalog.timing);
    }

    return catalog;
  } catch (error) {
    if (debug) console.warn('elevation-catalog: request threw', error);
    return null;
  }
}
